import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
// material
import { Card, Container, Stack, Table, TableBody, TableContainer, TableRow } from '@mui/material';
import { Box } from '@mui/system';
// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserListHead, UserMoreMenu } from '../../components/_dashboard/user';
import { fDate, fDateTime2 } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';
import PolicyToolbar from './PolicyToolbar';
import { StyledTableCell, StyledTableRow } from '../../utils/StyleTable';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getPolicysList } from '../../store/Slice/Policy/PolicySlice';
import usePolicySelector from '../../store/Slice/Policy/PolicySelector';
import {
  getAllEmpMasterList,
  getBankDropdownData,
  getLoanTypeData
} from '../../store/Slice/Global/GlobalSlice';
import { isValidValue, normalizeValue } from '../../utils/CommonFunction';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'id', label: 'Policy ID', alignRight: false },
  { id: 'name', label: 'Bank Name', alignRight: false },
  { id: 'loanType', label: 'Loan Type', alignRight: false },
  { id: 'employmentType', label: 'Employment Type', alignRight: false },
  { id: 'interestRate', label: 'Interest Rate', alignRight: false },
  { id: 'processingFees', label: 'Processing Fee', alignRight: false },
  { id: 'created', label: 'Start Date', alignRight: false },
  { id: 'created_end', label: 'End Date', alignRight: false },
  { id: 'action', label: 'ACTION' }
];

export default function PolicyList(parent) {
  const [page, setPage] = useState(0);
  const [page1, setPage1] = useState(1);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(600);
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [filterParameter, setFilterParameter] = useState({
    loanTypeId: ' ',
    employmentSubTypeId: ' ',
    bankId: null
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { PolicyListData } = usePolicySelector();

  const getPolicysListApi = useCallback(() => {
    const credentials = {
      userId: LoginData?.userId,
      companyId: LoginData?.companyId,
      accessLevelId: LoginData?.accessLevelId,
      paginationModel: {
        searchLiteral: filterName,
        pageSize: rowsPerPage,
        pageNumber: page1,
        sortOrder: order
      },
      policyFilterModel: {
        loanSubTypeId: filterParameter?.loanTypeId || null,
        employmentSubTypeId: filterParameter?.employmentSubTypeId || null,
        bankId: filterParameter?.bankId || null,
        fromLeadDate:
          filterDatesRange[0].startDate !== null ? fDateTime2(filterDatesRange[0].startDate) : null,
        toLeadDate:
          filterDatesRange[0].endDate !== null ? fDateTime2(filterDatesRange[0].endDate) : null
      }
    };

    dispatch(getPolicysList(credentials));
  }, [LoginData, filterName, order, filterDatesRange, filterParameter]);

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [dispatch, LoginData]);

  // all employee  master list
  const getEmployeeMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId
    };
    dispatch(getAllEmpMasterList(obj));
  }, [LoginData]);

  // loan master
  const getLoanMasterApi = useCallback(() => {
    const obj = {
      companyId: LoginData?.companyId
    };
    dispatch(getLoanTypeData(obj));
  }, [LoginData]);

  useEffect(() => {
    getLoanMasterApi();
  }, [getLoanMasterApi]);

  useEffect(() => {
    getPolicysListApi();
  }, [getPolicysListApi]);

  useEffect(() => {
    getBankDropApi();
  }, [getBankDropApi]);

  useEffect(() => {
    getEmployeeMasterApi();
  }, [getEmployeeMasterApi]);

  const handleRequestSort = (event, property) => {
    if (property !== 'action') {
      setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = PolicyListData?.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - PolicyListData.length) : 0;

  return (
    <Page title="Partner Managemet System || Agreement">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Policy" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>
          <Card>
            <PolicyToolbar
              parent="policy"
              filterName={filterName}
              setFilterName={setFilterName}
              filterDatesRange={filterDatesRange}
              setFilterDatesRange={setFilterDatesRange}
              setFilterParameter={setFilterParameter}
              filterParameter={filterParameter}
            />

            <Scrollbar>
              <TableContainer sx={{ minWidth: 800, height: '70vh', overflow: 'auto' }}>
                <Table>
                  <UserListHead
                    order={order}
                    orderBy={orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={PolicyListData?.length}
                    numSelected={selected.length}
                    onRequestSort={handleRequestSort}
                    onSelectAllClick={handleSelectAllClick}
                    parent="policy"
                  />
                  <TableBody>
                    {PolicyListData && PolicyListData?.length > 0
                      ? PolicyListData?.map((item, index) => {
                          const isItemSelected = selected.indexOf(item) !== -1;
                          return (
                            <StyledTableRow
                              hover
                              key={item}
                              tabIndex={-1}
                              role="checkbox"
                              selected={isItemSelected}
                              aria-checked={isItemSelected}
                            >
                              <StyledTableCell align="left" style={{ paddingLeft: '25px' }}>
                                {normalizeValue(item?.aggrementPolicyId, NOT_AVAILABLE)}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {normalizeValue(item?.bank?.bankName, NOT_AVAILABLE)}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {normalizeValue(item?.loanSubType?.loanName, NOT_AVAILABLE)}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {normalizeValue(
                                  item?.employmentSubType?.employmentSubcategoryName,
                                  NOT_AVAILABLE
                                )}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {normalizeValue(item?.interestRateMax, NOT_AVAILABLE)}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {normalizeValue(item?.preProcessingCharge, NOT_AVAILABLE)}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {isValidValue(item?.policyStartDate)
                                  ? fDate(item?.policyStartDate)
                                  : NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell align="left">
                                {isValidValue(item?.policyEndDate)
                                  ? fDate(item?.policyEndDate)
                                  : NOT_AVAILABLE}
                              </StyledTableCell>
                              <StyledTableCell align="" width={90}>
                                <UserMoreMenu
                                  parent="policy"
                                  partner={item}
                                  rowId={item?.aggrementPolicyId}
                                />
                              </StyledTableCell>
                            </StyledTableRow>
                          );
                        })
                      : ''}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <StyledTableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>

                  {PolicyListData?.length === 0 && (
                    <TableBody>
                      <TableRow>
                        <StyledTableCell
                          align="center"
                          colSpan={TABLE_HEAD.length + 1}
                          sx={{ py: 3 }}
                        >
                          <SearchNotFound searchQuery={filterName} />
                        </StyledTableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </Scrollbar>
          </Card>
        </Box>
      </Container>
    </Page>
  );
}
